import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { ArrowLeft, ExternalLink, CheckCircle, XCircle, MinusCircle, ClipboardCheck, RotateCcw, Lock, History, ChevronDown, ChevronUp, Keyboard } from 'lucide-react';

const AUDIT_ITEMS = [
  { key: 'signed_est',       label: 'Signed Estimate',              desc: 'Signed estimate present and attached to job' },
  { key: 'compliance_pic',   label: 'Compliance Picture',           desc: 'General compliance photo uploaded to job' },
  { key: 'stepback_outdoor', label: 'Stepback Pic — Outdoor Unit',  desc: 'Stepback photo showing full outdoor unit in frame' },
  { key: 'closeup_outdoor',  label: 'Closeup — Outdoor Data Plate', desc: 'Closeup photo of outdoor unit data/model plate' },
  { key: 'stepback_indoor',  label: 'Stepback Pic — Indoor Unit',   desc: 'Stepback photo showing full indoor unit in frame' },
  { key: 'tstat_pic',        label: 'TSTAT Picture',                desc: 'Photo of thermostat screen/settings' },
  { key: 'panel_pic',        label: 'Panel Picture',                desc: 'Photo of electrical panel' },
  { key: 'inv_sig',          label: 'Invoice Signature',            desc: 'Customer signature on invoice present' },
  { key: 'power_ts',         label: 'PowerTs',                      desc: 'PowerT form completed on the job' },
  { key: 'parts_process',    label: 'Parts Process',                desc: 'Parts process followed correctly per SOP' },
  { key: 'job_costing',      label: 'Job Costing',                  desc: 'Labor hours, materials, and revenue posted correctly' },
  { key: 'rsr_checklist',    label: 'RSR System Checklist',         desc: 'RSR system checklist completed in ServiceTitan' },
];

function TriToggle({ value, onChange }) {
  const opts = [
    { v: 'Yes', icon: CheckCircle, color: 'text-green-600 bg-green-50 border-green-200', active: 'bg-green-500 text-white border-green-500' },
    { v: 'No',  icon: XCircle,     color: 'text-red-600 bg-red-50 border-red-200',       active: 'bg-red-500 text-white border-red-500' },
    { v: 'N/A', icon: MinusCircle, color: 'text-gray-500 bg-gray-50 border-gray-200',    active: 'bg-gray-400 text-white border-gray-400' },
  ];
  return (
    <div className="flex gap-2">
      {opts.map(({ v, icon: Icon, color, active }) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(value === v ? null : v)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-all
            ${value === v ? active : color}`}
        >
          <Icon size={14} />
          {v}
        </button>
      ))}
    </div>
  );
}

export default function AuditForm() {
  const { jobId }  = useParams();
  const navigate   = useNavigate();
  const [job,      setJob]      = useState(null);
  const [audit,    setAudit]    = useState({});
  const [notes,    setNotes]    = useState('');
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [completing, setCompleting] = useState(false);
  const [history,    setHistory]    = useState([]);
  const [showHistory,setShowHistory]= useState(false);
  const [customItems, setCustomItems] = useState([]);
  const [customAnswers, setCustomAnswers] = useState({});
  const [activeRow, setActiveRow] = useState(0);
  const [showShortcuts, setShowShortcuts] = useState(false);

  useEffect(() => {
    api.get(`/jobs/${jobId}`)
      .then(r => {
        setJob(r.data.job);
        if (r.data.audit) {
          const a = r.data.audit;
          const vals = {};
          AUDIT_ITEMS.forEach(({ key }) => { vals[key] = a[key] || null; });
          setAudit(vals);
          setNotes(a.notes || '');
          if (a.custom_answers) {
            try { setCustomAnswers(JSON.parse(a.custom_answers)); } catch {}
          }
        }
      })
      .finally(() => setLoading(false));

    api.get(`/audits/${jobId}/history`).then(r => setHistory(r.data || [])).catch(() => {});
    api.get('/checklist-items/').then(r => setCustomItems(r.data || [])).catch(() => {});

    // Unlock when leaving the page
    return () => { api.post(`/jobs/${jobId}/unlock`).catch(() => {}); };
  }, [jobId]);

  const customVals = Object.values(customAnswers).filter(v => v && v !== null);
  const answered   = [
    ...AUDIT_ITEMS.filter(({ key }) => audit[key] && audit[key] !== null).map(({ key }) => audit[key]),
    ...customVals,
  ];
  const yesCount   = answered.filter(v => v === 'Yes').length;
  const noCount    = answered.filter(v => v === 'No').length;
  const applicable = answered.filter(v => v !== 'N/A').length;
  const score      = applicable > 0 ? Math.round((yesCount / applicable) * 100) : null;
  const result     = applicable > 0 ? (noCount > 0 ? 'Flag' : 'Pass') : null;

  // Combined checklist rows (built-in + custom) for keyboard navigation/grading
  const allRows = [
    ...AUDIT_ITEMS.map(({ key, label }) => ({ key, label, custom: false })),
    ...customItems.map(({ field_key, label }) => ({ key: field_key, label, custom: true })),
  ];

  function gradeRow(idx, value) {
    const row = allRows[idx];
    if (!row) return;
    if (row.custom) setCustomAnswer(row.key, value);
    else setAudit(a => ({ ...a, [row.key]: value }));
  }

  // Keyboard shortcuts: Up/Down (or J/K) to move, Y/N/A (or 1/2/3) to grade + auto-advance
  useEffect(() => {
    function onKeyDown(e) {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (job?.audit_status === 'completed') return;

      const key = e.key.toLowerCase();
      if (key === 'arrowdown' || key === 'j') {
        e.preventDefault();
        setActiveRow(i => Math.min(i + 1, allRows.length - 1));
      } else if (key === 'arrowup' || key === 'k') {
        e.preventDefault();
        setActiveRow(i => Math.max(i - 1, 0));
      } else if (key === 'y' || key === '1') {
        e.preventDefault();
        gradeRow(activeRow, 'Yes');
        setActiveRow(i => Math.min(i + 1, allRows.length - 1));
      } else if (key === 'n' || key === '2') {
        e.preventDefault();
        gradeRow(activeRow, 'No');
        setActiveRow(i => Math.min(i + 1, allRows.length - 1));
      } else if (key === 'a' || key === '3') {
        e.preventDefault();
        gradeRow(activeRow, 'N/A');
        setActiveRow(i => Math.min(i + 1, allRows.length - 1));
      } else if (key === '?') {
        e.preventDefault();
        setShowShortcuts(s => !s);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeRow, allRows.length, customItems, job]);

  function setCustomAnswer(field_key, value) {
    setCustomAnswers(prev => ({ ...prev, [field_key]: value }));
  }

  async function handleSubmit() {
    setSaving(true);
    try {
      const payload = { ...audit, notes, custom_answers: customAnswers };
      const r = await api.post(`/audits/${jobId}`, payload);
      toast.success(`Saved — ${r.data.result} ${r.data.score_pct ? `(${r.data.score_pct})` : ''}`);
      navigate('/queue');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleComplete() {
    setCompleting(true);
    try {
      await api.post(`/jobs/${jobId}/complete`);
      toast.success('Audit marked complete');
      navigate('/completed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not complete');
    } finally {
      setCompleting(false);
    }
  }

  async function handleSaveAndComplete() {
    setSaving(true);
    setCompleting(true);
    try {
      const payload = { ...audit, notes, custom_answers: customAnswers };
      await api.post(`/audits/${jobId}`, payload);
      await api.post(`/jobs/${jobId}/complete`);
      toast.success('Audit saved and marked complete');
      navigate('/completed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save & complete');
    } finally {
      setSaving(false);
      setCompleting(false);
    }
  }

  async function handleReopen() {
    setCompleting(true);
    try {
      await api.post(`/jobs/${jobId}/reopen`);
      toast.success('Audit reopened');
      navigate('/queue');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reopen');
    } finally {
      setCompleting(false);
    }
  }

  if (loading) return <div className="p-8 text-gray-400">Loading job...</div>;
  if (!job)    return <div className="p-8 text-red-500">Job not found</div>;

  const BRANCH_SLUG = {
    'Del-Air RNC':     'delairrnc',
    'Jacksonville':    'jacksonville105',
    'Orlando North':   'orlandonorth101',
    'Orlando South':   'orlandosouth102',
    'Orlando West':    'orlandowest103',
    'Sarasota':        'sarasota107',
    'Tampa':           'tampa106',
    'Vero Beach':      'verobeach109',
    'Volusia County':  'volusiacounty110',
  };
  // Use tenant_name slug if set (from dated file import), otherwise derive from branch
  const tenantSlug = (job.tenant_name && job.tenant_name.trim())
    ? job.tenant_name.toLowerCase().replace(/\s/g, '')
    : (BRANCH_SLUG[job.branch] || '');
  const stUrl = tenantSlug
    ? `https://${tenantSlug}.eh.go.servicetitan.com/#/Job/Index/${String(job.job_number).replace(/\.0$/, '')}`
    : null;

  return (
    <div className="p-8 max-w-4xl mx-auto space-y-6">
      {/* Back */}
      <button onClick={() => navigate('/queue')}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeft size={16} /> Back to Queue
      </button>

      {/* Job Info Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-xl font-bold text-gray-900">Job #{job.job_number}</h1>
              {stUrl && (
                <a href={stUrl} target="_blank" rel="noreferrer"
                  className="flex items-center gap-1 text-xs text-[#E8761A] hover:underline">
                  <ExternalLink size={12} /> ServiceTitan
                </a>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-4">
              {[
                ['Branch',        job.branch],
                ['Tech / RSC',    job.primary_tech],
                ['Job Type',      job.job_type],
                ['Business Unit', job.business_unit],
                ['Sched Date',    job.scheduled_date ? new Date(job.scheduled_date).toLocaleDateString() : '—'],
                ['Status',        job.status],
                ['Customer',      job.customer_name],
                ['Address',       job.customer_address],
                ['Jobs Total',    job.jobs_total ? `$${Number(job.jobs_total).toLocaleString()}` : '—'],
                ['Balance',       job.balance != null ? `$${Number(job.balance).toLocaleString()}` : '—'],
                ['Hours Worked',  job.hours_worked != null ? Number(job.hours_worked).toFixed(2) : '—'],
                ['Estimates',     job.estimates != null ? job.estimates : '—'],
              ].map(([k, v]) => (
                <div key={k}>
                  <div className="text-xs text-gray-400 font-medium">{k}</div>
                  <div className="text-gray-700 font-medium truncate">{v || '—'}</div>
                </div>
              ))}
            </div>

            {/* Summary / Notes from ServiceTitan */}
            {job.summary && job.summary !== '0' && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
                <div className="text-xs text-gray-400 font-medium mb-1">Job Summary</div>
                <div className="text-xs text-gray-600 whitespace-pre-wrap leading-relaxed">{job.summary}</div>
              </div>
            )}
          </div>

          {/* Live Score */}
          {result && (
            <div className={`flex-shrink-0 ml-6 text-center px-6 py-4 rounded-xl
              ${result === 'Pass' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className={`text-3xl font-bold ${result === 'Pass' ? 'text-green-600' : 'text-red-600'}`}>
                {score !== null ? `${score}%` : '—'}
              </div>
              <div className={`text-sm font-semibold mt-1 ${result === 'Pass' ? 'text-green-600' : 'text-red-600'}`}>
                {result}
              </div>
              <div className="text-xs text-gray-400 mt-1">{yesCount} Yes · {noCount} No</div>
            </div>
          )}
        </div>
      </div>

      {/* Edit History */}
      {history.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <button onClick={() => setShowHistory(s => !s)}
            className="w-full px-6 py-3 flex items-center justify-between text-sm font-semibold text-gray-700 hover:bg-gray-50">
            <span className="flex items-center gap-2">
              <History size={15} className="text-gray-400" />
              Edit History <span className="text-gray-400 font-normal">({history.length} prior version{history.length !== 1 ? 's' : ''})</span>
            </span>
            {showHistory ? <ChevronUp size={15} className="text-gray-400" /> : <ChevronDown size={15} className="text-gray-400" />}
          </button>
          {showHistory && (
            <div className="divide-y divide-gray-50">
              {history.map(h => {
                const s = h.snapshot || {};
                return (
                  <div key={h.id} className="px-6 py-3 text-xs">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-gray-600">
                        Edited by {h.changed_by_name || 'Unknown'}
                      </span>
                      <span className="text-gray-400">
                        {h.changed_at ? new Date(h.changed_at).toLocaleString() : ''}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-gray-500">
                      <span>Previous result: <span className={`font-semibold ${s.result === 'Pass' ? 'text-green-600' : s.result === 'Flag' ? 'text-red-600' : 'text-gray-400'}`}>{s.result || '—'}</span></span>
                      <span>Previous score: <span className="font-semibold text-gray-600">{s.score != null ? `${Math.round(s.score * 100)}%` : '—'}</span></span>
                    </div>
                    {s.notes && (
                      <div className="mt-1.5 px-3 py-2 bg-gray-50 border border-gray-100 rounded-lg text-gray-600">
                        <span className="font-semibold text-gray-400">Previous notes: </span>{s.notes}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Checklist */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-[#3D2B1A] flex items-center justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold text-white">Audit Checklist</h2>
            <p className="text-xs text-white/50 mt-0.5">Select Yes, No, or N/A for each item</p>
          </div>
          <button type="button" onClick={() => setShowShortcuts(s => !s)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/10 text-white hover:bg-white/20 transition-colors">
            <Keyboard size={14} /> Shortcuts
          </button>
        </div>
        {showShortcuts && (
          <div className="px-6 py-3 bg-amber-50 border-b border-amber-100 text-xs text-amber-800 flex flex-wrap gap-x-6 gap-y-1.5">
            <span><kbd className="px-1.5 py-0.5 bg-white border border-amber-200 rounded font-mono">↑</kbd> / <kbd className="px-1.5 py-0.5 bg-white border border-amber-200 rounded font-mono">↓</kbd> or <kbd className="px-1.5 py-0.5 bg-white border border-amber-200 rounded font-mono">J</kbd>/<kbd className="px-1.5 py-0.5 bg-white border border-amber-200 rounded font-mono">K</kbd> — move between items</span>
            <span><kbd className="px-1.5 py-0.5 bg-white border border-amber-200 rounded font-mono">Y</kbd> / <kbd className="px-1.5 py-0.5 bg-white border border-amber-200 rounded font-mono">1</kbd> — grade Yes</span>
            <span><kbd className="px-1.5 py-0.5 bg-white border border-amber-200 rounded font-mono">N</kbd> / <kbd className="px-1.5 py-0.5 bg-white border border-amber-200 rounded font-mono">2</kbd> — grade No</span>
            <span><kbd className="px-1.5 py-0.5 bg-white border border-amber-200 rounded font-mono">A</kbd> / <kbd className="px-1.5 py-0.5 bg-white border border-amber-200 rounded font-mono">3</kbd> — grade N/A</span>
            <span>Grading auto-advances to the next item · click any row to jump to it</span>
            <span><kbd className="px-1.5 py-0.5 bg-white border border-amber-200 rounded font-mono">?</kbd> — toggle this panel</span>
          </div>
        )}
        <div className="divide-y divide-gray-50">
          {AUDIT_ITEMS.map(({ key, label, desc }, i) => (
            <div key={key} onClick={() => setActiveRow(i)}
              className={`px-6 py-4 flex items-center justify-between gap-6 cursor-pointer transition-colors
              ${i === activeRow ? 'bg-orange-50 ring-1 ring-inset ring-[#E8761A]/40' : (i % 2 === 0 ? 'bg-white' : 'bg-gray-50/40')}`}>
              <div className="min-w-0 flex-1 flex items-center gap-2">
                {i === activeRow && <span className="w-1.5 h-1.5 rounded-full bg-[#E8761A] flex-shrink-0" />}
                <div>
                  <div className="font-medium text-gray-800 text-sm">{label}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{desc}</div>
                </div>
              </div>
              <TriToggle
                value={audit[key] || null}
                onChange={v => setAudit(a => ({ ...a, [key]: v }))}
              />
            </div>
          ))}
          {customItems.map(({ id, field_key, label, description }, i) => {
            const rowIdx = AUDIT_ITEMS.length + i;
            return (
            <div key={`custom-${id}`} onClick={() => setActiveRow(rowIdx)}
              className={`px-6 py-4 flex items-center justify-between gap-6 cursor-pointer transition-colors
              ${rowIdx === activeRow ? 'bg-orange-50 ring-1 ring-inset ring-[#E8761A]/40' : (rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40')}`}>
              <div className="min-w-0 flex-1 flex items-center gap-2">
                {rowIdx === activeRow && <span className="w-1.5 h-1.5 rounded-full bg-[#E8761A] flex-shrink-0" />}
                <div>
                  <div className="font-medium text-gray-800 text-sm flex items-center gap-2">
                    {label}
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-orange-100 text-[#E8761A]">Custom</span>
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">{description}</div>
                </div>
              </div>
              <TriToggle
                value={customAnswers[field_key] || null}
                onChange={v => setCustomAnswer(field_key, v)}
              />
            </div>
            );
          })}
        </div>
      </div>

      {/* Completed banner */}
      {job.audit_status === 'completed' && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-green-700">
            <Lock size={16} />
            <span className="text-sm font-medium">
              Completed {job.completed_at ? new Date(job.completed_at).toLocaleDateString() : ''}
              {job.completed_by_name ? ` by ${job.completed_by_name}` : ''}
            </span>
          </div>
          <button onClick={handleReopen} disabled={completing}
            className="flex items-center gap-1.5 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg disabled:opacity-40">
            <RotateCcw size={14} /> {completing ? 'Reopening...' : 'Reopen Audit'}
          </button>
        </div>
      )}

      {/* Notes */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          disabled={job.audit_status === 'completed'}
          rows={3}
          placeholder="Add any notes about this job..."
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E8761A] resize-none disabled:bg-gray-50 disabled:text-gray-400"
        />
      </div>

      {/* Submit / Complete actions */}
      {job.audit_status !== 'completed' && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">
            {answered.length} of {AUDIT_ITEMS.length} items answered
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleSubmit}
              disabled={saving || answered.length === 0}
              className="px-6 py-2.5 bg-[#3D2B1A] hover:bg-[#E8761A] text-white font-medium rounded-lg transition-colors disabled:opacity-40"
            >
              {saving ? 'Saving...' : 'Save Audit'}
            </button>
            {(job.audit_status === 'audited' || job.audit_status === 'flagged') && (
              <button
                onClick={handleComplete}
                disabled={completing}
                className="flex items-center gap-2 px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors disabled:opacity-40"
              >
                <ClipboardCheck size={16} />
                {completing ? 'Completing...' : 'Mark Complete'}
              </button>
            )}
            <button
              onClick={handleSaveAndComplete}
              disabled={saving || completing || answered.length === 0}
              className="flex items-center gap-2 px-6 py-2.5 bg-[#E8761A] hover:bg-[#3D2B1A] text-white font-medium rounded-lg transition-colors disabled:opacity-40"
            >
              <ClipboardCheck size={16} />
              {saving || completing ? 'Saving & Completing...' : 'Save & Complete'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
